import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from "react-router-dom";
import { Glassware } from '../models/glassware';
import { LookupItem } from '../models/lookup-item';
import { Vendor } from '../models/vendor';
import { Document } from '../models/document';
import { DocumentType } from '../models/enums';
import { GlasswareItem } from '../models/glassware-item';
import DocumentLoader from './DocumentLoader';
import { CrudFunctions } from '../utilities/crud-functions';
import { PWDates } from '../utilities/pw-dates';
import { DocumentationConstants } from '../store/documentation-constants';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';
import RowHistoryPopup, { RowHistoryPopupRef } from './Auditing/RowHistoryPopup';

import DataGrid, {
    Column,
    FilterRow,
    GroupPanel,
    Selection,
    HeaderFilter,
    Editing,
    Popup,
    Form,
    Toolbar,
    Item as ToolBarItem,
    Button as ColumnButton,
    Paging,
    Scrolling,
    Lookup,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Toast } from 'devextreme-react/toast';

export default function GlasswareForm() {
    const [vendors, setVendors] =
        useState<Vendor[]>();

    const [glassware, setGlassware] =
        useState<Glassware[]>();

    const [selectedGlassware, setSelectedGlassware] =
        useState<Glassware>();

    const [glasswareItems, setGlasswareItems] =
        useState<GlasswareItem[]>();

    const [selectedGlasswareItem, setSelectedGlasswareItem] =
        useState<GlasswareItem>();

    const [glasswareTypes, setGlasswareTypes] =
        useState<LookupItem[]>();

    //  const [currentGlasswareCertificate, setCurrentGlasswareCertificate] =
    //    useState<Document[]>();

    const [toastVisible, setToastVisible] =
        useState<boolean>();

    const [toastMessage, setToastMessage] =
        useState<string>();

    const [toastType, setToastType] =
        useState<any>();

    const [certicatesDialogVisible, setCerticatesDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getGlasswareItems();
    }, [selectedGlassware]);

    useEffect(() => {
        getAllData();
    }, []);

    const rowHistoryPopupRef = useRef<RowHistoryPopupRef>(null);

    return (
        <div className="pw-content">
            <div className="glassware-container">
                <div className="lq-title-bar pw-form-header">
                    <div className="lq-main-title">Glassware</div>
                    <div className="title-bar-spacer" />
                    <div className="save-toolbar">
                        <div>
                            <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                                hint="go back" className="title-bar-button"
                                height={'2.6em'}
                                onClick={goBack} />
                        </div>
                        <div>
                            <NavLink to={`/documentation/${DocumentationConstants.GlasswareForm}`}
                                className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                                <Button className="title-bar-button pw-orange"
                                    text="" icon="help" type="normal"
                                    hint="help and documentation" />
                            </NavLink>
                        </div>
                    </div>
                </div>

                <div className="pw-margin-bottom-1">
                    <DataGrid height="20rem" width="100%"
                        dataSource={glassware != null ? glassware : []}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                        onSelectionChanged={glasswareSelectionChanged}
                        onRowValidating={onAssetsRowValidating}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Paging enabled={false} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <GroupPanel visible={true} />
                        <SearchPanel visible={true} />
                        <Scrolling columnRenderingMode={'virtual'} />

                        <Column type="buttons" width="7rem">
                            <ColumnButton name="edit" />
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={onGlasswareAuditButtonClicked} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onGlasswareInformationButtonClicked} />
                        </Column>
                        <Column dataField='name' allowEditing={false} width='20%' />
                        <Column dataField='glasswareType' allowEditing={false} width='20%' groupIndex={0} >
                            <Lookup dataSource={glasswareTypes}
                                allowClearing={true}
                                valueExpr='id'
                                displayExpr='name' />
                        </Column>

                        <Column dataField='volume' width='15%' />
                        <Column dataField='high' width='15%' />
                        <Column dataField='low' width='15%' />
                        <Column dataField='tolerance' width='15%' />

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={false}
                            allowUpdating={true}
                            allowDeleting={false} >

                            <Popup title="Glassware" showTitle={true} width='30rem' height='18rem' />
                            <Form>
                                <Item dataField="name" />
                                <Item dataField="glasswareType" />
                                <Item dataField="volume" />
                                <Item dataField="high" />
                                <Item dataField="low" />
                                <Item dataField="tolerance" />
                            </Form>
                        </Editing>
                    </DataGrid>
                </div>

                <div>
                    <DataGrid height="20rem" width="100%"
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        dataSource={glasswareItems ? glasswareItems : []}
                        onInitNewRow={onGlasswareItemInitNewRow}
                        onSelectionChanged={onSelectedGlasswareItemChanged}
                        onRowValidating={onGlasswareItemsDatagridRowValidating}
                        onRowRemoving={onGlasswareItemRemoving}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}
                        keyExpr='glasswareItemId' >

                        <LoadPanel enabled={true} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />
                        <Paging enabled={false} />
                        <Scrolling showScrollbar="always" columnRenderingMode={'virtual'} />

                        <Column type="buttons" width="8rem">
                            <ColumnButton name="edit" />
                            <ColumnButton name="delete" />
                            <ColumnButton hint="attach document" icon="attach"
                                onClick={showGlasswareCertificatesDialog}
                                render={renderDocumentsColumnButton} />
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={onGlasswareItemsAuditButtonClicked} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onGlasswareItemsInformationButtonClicked} />
                        </Column>

                        <Column dataField='name' width='10rem' />
                        <Column dataField='dateAcquired' caption="Validation Date"
                            dataType="date" format="MM/dd/yyyy" width='7rem' />
                        <Column dataField='revalidationDate' dataType="date" format="MM/dd/yyyy"
                            width='7rem' cellRender={renderGlasswareRevalidationCell} />
                        <Column dataField='dateDestroyed' dataType="date" format="MM/dd/yyyy" width='7rem' />

                        <Column dataField='vendorId' caption="Vendor" dataType="string" width='7rem'>
                            <Lookup dataSource={vendors}
                                allowClearing={true}
                                valueExpr='vendorId'
                                displayExpr='name' />
                        </Column>

                        <Column dataField='modelNumber' caption="Model#" dataType="string" width='7rem' />
                        <Column dataField='serialNumber' caption="Serial #" dataType="string" width='7rem' />
                        <Column dataField='internalNo' caption="Internal#" dataType="string" width='7rem' />

                        <Column dataField='pressure' dataType="number" width='6rem' />
                        <Column dataField='temperature' dataType="number" width='7rem' />
                        <Column dataField='actualWeight' dataType="number" width='6rem' />
                        <Column dataField='correctedVolume' dataType="number" width='6rem'
                            allowEditing={false} />
                        <Column dataField='correctedVolumeCertificate' caption="Certificate" dataType="boolean" width='7rem' />
                        <Column dataField='correctedVolumeOutOfRange' caption="Out of Range"
                            dataType="boolean" width='6rem' allowEditing={false} />
                        <Column dataField='correctionFactorUsed' caption="Correction Factor"
                            dataType="number" width='6rem' allowEditing={false} />
                        <Column dataField='pass' dataType="boolean" width='6rem'
                            allowEditing={false} cellRender={renderGlasswareItemPassCell} />
                        <Column dataField='qc' dataType="boolean" width='6rem' caption="QC" />
                        <Column dataField='notes' width='15rem' />
                        <Column />

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Glassware Items</div>
                                </div>
                            </ToolBarItem>
                            <ToolBarItem location="after" name="searchPanel" />
                            <ToolBarItem location="after" disabled={!selectedGlassware} name="addRowButton" />
                        </Toolbar>

                        <Editing
                            mode="popup"
                            useIcons={true}
                            allowAdding={selectedGlassware != null}

                            allowUpdating={true}
                            allowDeleting={true} >

                            <Popup title="Glassware Item" showTitle={true} width="80vw" height="80vh" />
                            <Form>
                                <Item dataField="name" />
                                <Item dataField="dateAcquired" />
                                <Item dataField="revalidationDate" />
                                <Item dataField="dateDestroyed" />
                                <Item dataField="vendorId" />
                                <Item dataField="modelNumber" />
                                <Item dataField="serialNumber" />
                                <Item dataField="internalNo" />
                                <Item dataField="pressure" />
                                <Item dataField="temperature" />
                                <Item dataField="actualWeight" />
                                <Item />
                                <Item dataField="correctedVolumeCertificate" />
                                <Item dataField="qc" caption="For QC Only" />
                                <Item dataField="notes" colSpan={2} />


                            </Form>
                        </Editing>
                    </DataGrid>
                </div>
            </div>

            <DmaFooter />

            <RowHistoryPopup ref={rowHistoryPopupRef} />

            <Toast
                visible={toastVisible}
                message={toastMessage}
                type={toastType}
                onHiding={onToastHiding}
                displayTime={600}
            />

            {certicatesDialogVisible &&
                <DialogPopup visible={certicatesDialogVisible}
                    onHiding={hideCertificatesDialog}
                    title="Glassware Certificate"
                    width={'35rem'}
                    height={'25rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <DocumentLoader
                        readOnly={false} canDelete={true}
                        parentId={selectedGlasswareItem && selectedGlasswareItem.glasswareItemId ?
                            selectedGlasswareItem.glasswareItemId : undefined}
                        documentType={DocumentType.GlasswareCertificate} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onToastHiding() {
        setToastVisible(false);
        setToastMessage(undefined);
        setToastType(undefined);
    }

    function showToast(message: string, toastType: string) {
        setToastMessage(message);
        setToastType(toastType);
        setToastVisible(true);
    }

    async function showGlasswareCertificatesDialog(e: any) {
        if (e.row && e.row.data) {
            setSelectedGlasswareItem(e.row.data);

            const glasswareItemId = e.row.data.glasswareId;

            if (glasswareItemId) {
                await getGlasswareCertificate(glasswareItemId)
                    .then(data => {
                        //setCurrentGlasswareCertificate(data);
                        setCerticatesDialogVisible(true);
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Retrieving Glassware Certificate");
                    });
            }
        }
    }

    /*
    async function onGlasswareCerticateDelete(documentId: string) {
        if (documentId) {
            await deleteGlasswareCertificate(documentId)
                .then(() => {
                    showToast("Glassware Certificate deleted", "success");
                     
                    hideCertificatesDialog();
                })
                .catch((response: ResponseError) => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                });
        }
    }

    function hideGlasswareCertificatesDialog() {
        setCerticatesDialogVisible(false);
    }

    async function onGlasswareCertificateLoaded(doc: Document) {

        if (selectedGlassware && selectedGlassware.glasswareId) {
            doc.parentId = selectedGlassware.glasswareId
            doc.documentType = DocumentType.GlasswareCertificate;

            await insertGlasswareCertificate(doc)
                .then(() => {
                    showToast("Glassware Certificate saved", "success");
                    hideCertificatesDialog();
                })
                .catch((response: ResponseError) => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                });
        }
    }
    */
    function onGlasswareAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            rowHistoryPopupRef.current?.open('Glassware', e.row!.data.glasswareId);
        }
    }

    function onGlasswareInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
        }
    }

    function onGlasswareItemsAuditButtonClicked(e: any) {
        if (e.row && e.row.data) {
            rowHistoryPopupRef.current?.open('GlasswareItems', e.row!.data.glasswareItemId);
        }
    }

    function onGlasswareItemsInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
        }
    }

    //change

    function glasswareSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedGlassware = e.selectedRowsData[0];
            setSelectedGlassware(selectedGlassware);
        }
    }

    function onSelectedGlasswareItemChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            const selectedGlasswareItem = e.selectedRowsData[0];

            setSelectedGlasswareItem(selectedGlasswareItem);
        }
    }

    // validating

    async function onAssetsRowValidating(e: any) {
        if (e.oldData) {
            let glassware = e.oldData as Glassware;

            if (glassware) {
                let updatedGlassware = { ...glassware, ...e.newData };
                await updateGlassware(updatedGlassware)
                    .then(() => {
                        showToast("Glassware updated", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Glassware");
                    });
            }
        }
    }

    function onGlasswareItemInitNewRow(e: any) {
        let newGlasswareItem: GlasswareItem = {
            correctedVolumeCertificate: false,
            correctedVolumeOutOfRange: false,
            qc: false,
            pass: false
        };

        e.data = newGlasswareItem;
    }

    async function onGlasswareItemsDatagridRowValidating(e: any) {
        if (e.oldData) {
            let glasswareItem = e.oldData as GlasswareItem;

            if (glasswareItem) {
                let updatedGlasswareItem = { ...glasswareItem, ...e.newData };

                updateGlasswareItem(updatedGlasswareItem)
                    .then(() => {
                        showToast("Glassware Item updated", "success");
                        getGlasswareItems();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Glassware Item");
                    });
            }
        }
        else {
            if (e.newData) {
                let glasswareItem = e.newData as GlasswareItem;

                if (glasswareItem && selectedGlassware && selectedGlassware.glasswareId) {
                    glasswareItem.glasswareId = selectedGlassware.glasswareId;

                    await insertGlasswareItem(glasswareItem)
                        .then(() => {
                            showToast("Glassware Item saved", "success");
                            getGlasswareItems();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting Glassware Item");
                        });
                }
            }
        }
    }

    // delete

    function onGlasswareItemRemoving(e: any) {
        if (e.data && e.data.glasswareItemId)
            deleteGlasswareItem(e.data.glasswareItemId)
                .then(() => {
                    showToast("Glassware Item deleted", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Glassware Item");
                });
    }

    // get functions

    async function getGlassware() {
        await CrudFunctions.getData("common/glassware")
            .then(data => {
                setGlassware(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Glassware");
            });
    }

    async function getGlasswareItems() {
        if (selectedGlassware && selectedGlassware.glasswareId)
            await CrudFunctions.getData(`common/glasswareitems/${selectedGlassware.glasswareId}`)
                .then(data => {
                    setGlasswareItems(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Glassware Items");
                });
    }

    async function getGlasswareTypes() {
        await CrudFunctions.getData("common/glasswaretypes")
            .then(data => {
                setGlasswareTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Glassware Types");
            });
    }

    async function getVendors() {
        await CrudFunctions.getData("common/vendors")
            .then(data => {
                setVendors(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Vendors");
            });
    }

    async function getGlasswareCertificate(glasswareItemId: string) {
        return await CrudFunctions.getData(`media/documentsbyparentid/${glasswareItemId}/${DocumentType.GlasswareCertificate}`);
    }

    //POST functions

    async function insertGlasswareItem(glasswareItem: GlasswareItem) {
        return CrudFunctions.postData("common/glasswareitem", glasswareItem);
    }

    /*
    async function insertGlasswareCertificate(document: Document) {
        return await CrudFunctions.postData("media/document", document);
    }
    */

    //PUT functions

    async function updateGlassware(glassware: Glassware) {
        return CrudFunctions.putData("common/glassware", glassware);
    }

    async function updateGlasswareItem(glasswareItem: GlasswareItem) {
        return CrudFunctions.putData("common/glasswareitem", glasswareItem);
    }

    // DELETE functions

    async function deleteGlasswareItem(glasswareItemId: string) {
        return CrudFunctions.deleteData(`common/glasswareitem/${glasswareItemId}`);
    }

    /*
    async function deleteGlasswareCertificate(documentId: string) {
        return await CrudFunctions.deleteData(`media/document/${documentId}`);
    }
    */
    //navigation

    function goBack() {
        history.back();
    }

    //render

    function renderGlasswareRevalidationCell(cellData: any) {
        const currentDate = new Date();
        const revalidationDate = cellData.data.revalidationDate;

        if (revalidationDate) {
            const daysDifference = PWDates.getDifferenceInDays(currentDate, new Date(revalidationDate))

            if (daysDifference >= 30)
                return (<div className="validation-pass">{PWDates.getLocalDateString(revalidationDate)}</div>);
            else if (daysDifference < 30 && daysDifference > 0)
                return (<div className="validation-warn">{PWDates.getLocalDateString(revalidationDate)}</div>);
            else
                return (<div className="validation-fail">{PWDates.getLocalDateString(revalidationDate)}</div>);
        }
        else {
            return (<div className="validation-warning">NA</div>);
        }
    }

    function renderGlasswareItemPassCell(cellData: any) {
        return (
            cellData.data.pass ?
                <div className="validation-pass">PASS</div> :
                <div className="validation-fail">FAIL</div>
        );
    }

    function renderDocumentsColumnButton(e: any) {
        if (e.data && e.data.documentCount > 0)
            return <><i className="dx-icon-attach pw-green cursor-pointer"></i><span className="pw-green pw-normal">{e.data.documentCount}</span></>;
        return <i className="dx-icon-attach cursor-pointer"></i>;
    }


    function hideCertificatesDialog() {
        setCerticatesDialogVisible(false);
    }

    async function getAllData() {
        await Promise.all([
            getGlasswareTypes(),
            getGlassware(),
            getVendors()
        ]);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
